<template>
  <!-- Карточка слайдера выгод: подарок за сумму заказа (рост среднего чека). -->
  <div
    v-if="nudge"
    class="gift-nudge"
    :style="{ '--benefit-tone': BENEFIT_TONES.giftTier }"
    data-testid="gift-tier-nudge"
  >
    <BasketGoalRing :percent="progressPercent" :tone="BENEFIT_TONES.giftTier">
      🎁
    </BasketGoalRing>
    <div class="gift-nudge__content">
      <!-- Достигнут шаг: подарок уже в корзине -->
      <template v-if="nudge.hasCurrent">
        <div class="gift-nudge__title">
          <span class="gift-nudge__accent">{{ nudge.currentGiftName }}</span> — в подарок!
        </div>
        <div v-if="nudge.hasNext" class="gift-nudge__subtitle">
          Добери ещё {{ nudge.toNext }} ₽ и получи «{{ nudge.nextGiftName }}»
        </div>
        <div v-else class="gift-nudge__subtitle">
          Максимальный подарок ваш 🎉
        </div>
      </template>

      <!-- Ещё не достигнут первый шаг -->
      <template v-else-if="nudge.hasNext">
        <div class="gift-nudge__title">
          Добери ещё <span class="gift-nudge__accent">{{ nudge.toNext }} ₽</span>
        </div>
        <div class="gift-nudge__subtitle">
          и получи «{{ nudge.nextGiftName }}» в подарок
        </div>
      </template>

      <!-- Прогресс до следующего шага -->
      <div v-if="nudge.hasNext" class="gift-nudge__bar">
        <div class="gift-nudge__bar-fill" :style="{ width: progressPercent + '%' }" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BasketGoalRing from './BasketGoalRing.vue'
import { BENEFIT_TONES } from './benefitTones'
import { useGiftTiers } from '@features/basket'

const { nudge } = useGiftTiers()

/** Доля прогресса до следующего порога (0–100). */
const progressPercent = computed(() => {
  const n = nudge.value
  if (!n) return 0

  // Следующего порога нет: либо взят максимальный подарок — кольцо полное,
  // либо порогов не настроено вовсе — кольцо пустое.
  if (!n.hasNext || !n.nextThreshold) return n.hasCurrent ? 100 : 0

  return Math.min(100, Math.round((n.base / n.nextThreshold) * 100))
})
</script>

<style scoped lang="scss">
// Геометрия и типографика — как у соседних карточек слайдера (.info-card),
// чтобы лента выглядела одним набором, а не четырьмя разными блоками.
.gift-nudge {
  @apply relative flex items-center gap-3 w-full px-4 py-3 overflow-hidden;
  border-radius: var(--app-radius-card, 24px);
  @apply shadow-sm hover:shadow-md;
  @apply transition-all duration-300 ease-out;
  @apply hover:-translate-y-1 hover:shadow-lg;
  @apply active:translate-y-0 active:scale-95 active:duration-150;

  // Фон, рамка и акценты — из тона карточки (--benefit-tone, см. benefitTones.ts),
  // теми же формулами, что у соседей по ленте: карточки должны различаться цветом,
  // а не оттенком случайно подобранного градиента.
  background: linear-gradient(
    to bottom right,
    rgb(var(--benefit-tone) / 0.14),
    rgb(var(--benefit-tone) / 0.05)
  );
  border: 1px solid rgb(var(--benefit-tone) / 0.3);

  // Кольцо прогресса живёт в BasketGoalRing — подъём иконки достаём :deep.
  &:hover :deep(.goal-ring__badge) {
    @apply scale-110 rotate-3;
  }

  &__content {
    @apply flex flex-col flex-1 min-w-0 gap-1;
  }

  &__title {
    @apply font-bold text-sm tracking-tight leading-tight;
    @apply text-gray-900 dark:text-white tabular-nums;
    @apply md:text-xs;

    @media (max-width: 768px) {
      @apply text-xs;
    }
  }

  &__accent {
    color: rgb(var(--benefit-tone));
  }

  &__subtitle {
    @apply text-xs tracking-wide leading-tight;
    @apply text-gray-600 dark:text-gray-400;

    @media (max-width: 768px) {
      font-size: 0.65rem;
    }
  }

  &__bar {
    @apply mt-0.5 h-1.5 w-full rounded-full overflow-hidden;
    background: rgb(var(--benefit-tone) / 0.18);
  }

  &__bar-fill {
    @apply h-full rounded-full;
    @apply transition-all duration-500 ease-out;
    background: rgb(var(--benefit-tone));
  }
}

// Сенсорные устройства: hover-подъём только мешает
@media (hover: none) and (pointer: coarse) {
  .gift-nudge:hover,
  .gift-nudge:hover :deep(.goal-ring__badge) {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .gift-nudge,
  .gift-nudge__bar-fill {
    transition: none;
  }

  .gift-nudge:hover,
  .gift-nudge:hover :deep(.goal-ring__badge) {
    transform: none;
  }
}
</style>
